import type {
  ConductorTask,
  SimpleTask
} from 'Automations/nodeDefinitions/conductor'
import { SIMULATABLE_TASK_NAMES } from '../../simulation.constants'
import type { SimulationWorkflow } from '../../simulation.types'
import { findTaskByReferenceName } from '../findTaskByReferenceName'
import { getTaskDependencies } from '../getTaskDependencies'
import type {
  DependencyCollection,
  UnresolvedDependency
} from './dependencyCollection.types'

/**
 * Looks up one task, then does the same for whatever that task's own data
 * reads.
 *
 *   record it -> seen already? --- yes ---> stop
 *                     | no
 *                simulatable? ----- no ---> mark unsupported
 *                     | yes
 *                keep the task -> resolve each task its `data` points at
 */
export const resolveDependency = ({
  collection,
  unresolved,
  workflow
}: {
  collection: DependencyCollection
  unresolved: UnresolvedDependency
  workflow: SimulationWorkflow
}): DependencyCollection => {
  const collectionWithUnresolvedDependencies = {
    ...collection,
    encountered: [...collection.encountered, unresolved]
  }
  if (collection.visited.includes(unresolved.taskReferenceName))
    return collectionWithUnresolvedDependencies

  const visited = [...collection.visited, unresolved.taskReferenceName]
  const task = findTaskByReferenceName({
    tasks: workflow.tasks,
    taskReferenceName: unresolved.taskReferenceName
  })

  if (!isSimulatableTask(task)) {
    return {
      ...collectionWithUnresolvedDependencies,
      visited,
      unsupported: [...collection.unsupported, unresolved.taskReferenceName]
    }
  }

  return getTaskDependencies(task).reduce<DependencyCollection>(
    (acc, taskReferenceName) =>
      resolveDependency({
        collection: acc,
        unresolved: { taskReferenceName, semanticTypes: [] },
        workflow
      }),
    {
      ...collectionWithUnresolvedDependencies,
      visited,
      tasks: { ...collection.tasks, [unresolved.taskReferenceName]: task }
    }
  )
}

const isSimulatableTask = (
  task: ConductorTask | undefined
): task is SimpleTask =>
  task?.type === 'SIMPLE' && SIMULATABLE_TASK_NAMES.includes(task.name)
